import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { works } from '@/mocks/works';
import { newsList } from '@/mocks/news';
import { profile } from '@/mocks/profile';
import WorkCard from '@/components/feature/WorkCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSeo from '@/hooks/useSeo';

type TabKey = 'works' | 'news' | 'profile';

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: 'works', label: '作品', icon: 'ri-film-line' },
  { key: 'news', label: '动态', icon: 'ri-newspaper-line' },
  { key: 'profile', label: '明星资料', icon: 'ri-user-star-line' },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<TabKey>('works');

  useEffect(() => {
    setInput(query);
  }, [query]);

  const keyword = query.trim().toLowerCase();

  const matchedWorks = useMemo(
    () =>
      keyword
        ? works.filter((w) =>
            [w.title, w.type, w.role, w.director, w.summary, ...w.tags]
              .join(' ')
              .toLowerCase()
              .includes(keyword)
          )
        : [],
    [keyword]
  );

  const matchedNews = useMemo(
    () =>
      keyword
        ? newsList.filter((n) =>
            [n.title, n.category, n.summary, n.content.join(' ')]
              .join(' ')
              .toLowerCase()
              .includes(keyword)
          )
        : [],
    [keyword]
  );

  const matchedProfile = useMemo(() => {
    if (!keyword) return [];
    const haystack = [
      profile.name,
      profile.nameEn,
      profile.intro,
      profile.introExtra,
      profile.tagline,
      ...profile.facts.map((f) => `${f.label}${f.value}`),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(keyword) ? [profile] : [];
  }, [keyword]);

  const counts: Record<TabKey, number> = {
    works: matchedWorks.length,
    news: matchedNews.length,
    profile: matchedProfile.length,
  };
  const total = counts.works + counts.news + counts.profile;

  useSeo({
    title: query ? `“${query}” 的搜索结果 - 吉泽明步影视资料站` : '搜索 - 吉泽明步影视资料站',
    description: query
      ? `在吉泽明步影视资料站中搜索“${query}”，共找到 ${total} 条相关结果，涵盖作品、动态与明星资料。`
      : '搜索吉泽明步的作品、影视动态与明星资料。',
    keywords: '站内搜索,吉泽明步,作品搜索,动态搜索,资料搜索',
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) {
      setSearchParams({ q });
    } else {
      setSearchParams({});
    }
  };

  const hasQuery = keyword.length > 0;
  const activeEmpty = hasQuery && counts[tab] === 0;

  return (
    <div className="w-full">
      <div className="border-b border-background-200/70 bg-background-100/60">
        <div className="container-page py-8 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            站内搜索
          </h1>
          <form onSubmit={submit} className="mt-6 flex max-w-2xl items-center gap-3">
            <div className="flex flex-1 items-center rounded-full border border-background-300/70 bg-background-50 pl-4 pr-2 py-1.5">
              <i className="ri-search-line text-foreground-500" aria-hidden="true"></i>
              <input
                type="text"
                name="q"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入作品名称、动态关键词或明星资料"
                aria-label="搜索关键词"
                className="flex-1 bg-transparent px-3 py-1.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-5 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 cursor-pointer"
            >
              <i className="ri-search-line" aria-hidden="true"></i>
              搜索
            </button>
          </form>
          {hasQuery && (
            <p className="mt-4 text-sm text-foreground-600">
              当前关键词：<span className="font-medium text-foreground-950">“{query}”</span>
              <span className="text-foreground-500"> · 共 {total} 条结果</span>
            </p>
          )}
        </div>
      </div>

      <div className="container-page py-8 md:py-10">
        <div className="flex flex-wrap items-center gap-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={[
                'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap cursor-pointer',
                tab === t.key
                  ? 'bg-primary-600 text-background-50'
                  : 'border border-background-300/70 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-700',
              ].join(' ')}
            >
              <i className={t.icon} aria-hidden="true"></i>
              {t.label}
              <span
                className={[
                  'rounded-full px-1.5 text-xs',
                  tab === t.key ? 'bg-background-50/20 text-background-50' : 'bg-secondary-100 text-secondary-700',
                ].join(' ')}
              >
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-8">
          {!hasQuery && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-search-line text-2xl" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">输入关键词开始搜索</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                可以搜索作品名称、类型、角色、动态标题或明星资料。试试输入「时间的皱褶」「悬疑」或「代表作品」。
              </p>
            </div>
          )}

          {activeEmpty && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-emotion-sad-line text-2xl" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                该分类下暂无匹配结果
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                试试切换到其他分类标签，或换一个关键词重新搜索。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Link
                  to="/works"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm text-background-50 whitespace-nowrap transition-colors hover:bg-primary-700 cursor-pointer"
                >
                  浏览全部作品
                </Link>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300/70 px-4 py-2 text-sm text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                >
                  查看影视动态
                </Link>
              </div>
            </div>
          )}

          {hasQuery && tab === 'works' && matchedWorks.length > 0 && (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {matchedWorks.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
          )}

          {hasQuery && tab === 'news' && matchedNews.length > 0 && (
            <ul className="flex flex-col gap-4">
              {matchedNews.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/news/${item.id}`}
                    className="group flex items-start gap-4 rounded-lg border border-background-200/70 bg-background-50 p-5 transition-colors hover:border-primary-300 cursor-pointer"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className="ri-article-line text-xl" aria-hidden="true"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-foreground-950 group-hover:text-primary-700 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2">{item.summary}</p>
                      <p className="mt-2 text-xs text-foreground-500">{item.date} · {item.category}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {hasQuery && tab === 'profile' && matchedProfile.length > 0 && (
            <Link
              to="/profile"
              className="group flex items-center gap-5 rounded-lg border border-background-200/70 bg-background-50 p-5 transition-colors hover:border-primary-300 cursor-pointer"
            >
              <div className="h-20 w-16 shrink-0 overflow-hidden rounded-md">
                <img
                  src={profile.avatar}
                  alt={`${profile.name} 个人形象照`}
                  title={`${profile.name} - 明星资料`}
                  className="h-full w-full object-top"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading text-lg font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                  {profile.name}
                </h3>
                <p className="mt-1 text-sm text-foreground-600 line-clamp-2">{profile.intro}</p>
              </div>
              <i className="ri-arrow-right-s-line shrink-0 text-foreground-400 group-hover:text-primary-600 transition-colors" aria-hidden="true"></i>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}